import { useMemo, useState } from "react";
import Button from "src/base-components/Button";
import Lucide from "src/base-components/Lucide";
import ColorPickerPanel from "./ColorPickerPanel";
import {
  applyWebsiteThemePreset,
  buildCustomThemePreset,
  saveCustomTheme,
} from "src/helpers/theme";
import { buildFourStopGradient } from "src/themes/presets";

interface CustomThemeBuilderProps {
  onSaved: (themeId: string) => void;
  onCancel: () => void;
}

type ColorTarget = "primary" | "secondary";

const CustomThemeBuilder = ({ onSaved, onCancel }: CustomThemeBuilderProps) => {
  const [themeName, setThemeName] = useState("My Custom Theme");
  const [primaryColor, setPrimaryColor] = useState("#095CFD");
  const [secondaryColor, setSecondaryColor] = useState("#FFA935");
  const [activeTarget, setActiveTarget] = useState<ColorTarget>("primary");

  const previewPreset = useMemo(
    () =>
      buildCustomThemePreset({
        name: themeName,
        primary: primaryColor,
        secondary: secondaryColor,
      }),
    [themeName, primaryColor, secondaryColor],
  );

  const previewGradient = buildFourStopGradient(previewPreset.colors, "135deg");

  const handleSave = () => {
    const preset = buildCustomThemePreset({
      name: themeName,
      primary: primaryColor,
      secondary: secondaryColor,
    });
    saveCustomTheme(preset);
    applyWebsiteThemePreset(preset);
    onSaved(preset.id);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-white/10 px-5 py-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-white">Create custom theme</h3>
          <p className="mt-0.5 text-xs text-slate-400">
            Pick primary and secondary colors
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg p-2 text-slate-400 transition hover:bg-white/5 hover:text-white"
          aria-label="Close custom theme builder"
        >
          <Lucide icon="X" className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
        <div
          className="overflow-hidden rounded-xl border border-white/10"
          style={{ background: previewGradient }}
        >
          <div className="space-y-2 px-3 py-5">
            <p className="text-center text-xs font-medium text-white">
              Welcome to TravoTera
            </p>
            <div
              className="mx-auto w-fit rounded-md px-3 py-1.5 text-[10px] font-semibold text-white shadow"
              style={{ backgroundColor: primaryColor }}
            >
              Primary Button
            </div>
          </div>
        </div>

        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-slate-400">
            Theme name
          </span>
          <input
            value={themeName}
            onChange={(event) => setThemeName(event.target.value)}
            className="w-full rounded-lg border border-white/10 bg-[#0d1017] px-3 py-2 text-sm text-white outline-none focus:border-sky-400/60"
            placeholder="My Custom Theme"
          />
        </label>

        <div className="grid grid-cols-2 gap-2 rounded-xl border border-white/10 bg-[#12141c] p-1">
          {(
            [
              { id: "primary", label: "Primary", color: primaryColor },
              { id: "secondary", label: "Secondary", color: secondaryColor },
            ] as const
          ).map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActiveTarget(item.id)}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-left transition ${
                activeTarget === item.id
                  ? "bg-white/10 text-white"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              <span
                className="h-5 w-5 rounded-md border border-white/15"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-xs font-semibold">{item.label}</span>
            </button>
          ))}
        </div>

        <div className="flex gap-1.5">
          {previewPreset.colors.map((color, index) => (
            <span
              key={`preview-chip-${index}`}
              title={color}
              className="h-8 flex-1 rounded-md border border-white/15"
              style={{ backgroundColor: color }}
            />
          ))}
        </div>

        {activeTarget === "primary" ? (
          <ColorPickerPanel
            compact
            label="Primary color"
            value={primaryColor}
            onChange={setPrimaryColor}
          />
        ) : (
          <ColorPickerPanel
            compact
            label="Secondary color"
            value={secondaryColor}
            onChange={setSecondaryColor}
          />
        )}
      </div>

      <div className="sticky bottom-0 flex gap-2 border-t border-white/10 bg-[#0f1117] px-5 py-3">
        <Button
          variant="primary"
          className="flex-1 border-0 text-white"
          style={{ background: previewGradient }}
          onClick={handleSave}
        >
          Save theme
        </Button>
        <Button
          variant="outline-secondary"
          className="border-white/15 text-slate-200 hover:bg-white/5"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default CustomThemeBuilder;
